<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges && edit"></alert-unsaved-changes>
    <v-form ref="classificationForm">
      <!-- Domain & SubDomain -->
      <v-row v-for="(item, index) in domainsList" :key="'domain-' + index" align="center">
        <v-col cols="12" md="5">
          <label>{{ $t('scientific-domain') }}</label>
          <p class="label-info">
            A named group values defining the scientific area of activity. This attribute is <br />
            bundled with Scientific Subdomain.
          </p>
          <v-autocomplete
            v-model="item.domain"
            :items="domains"
            outlined
            :rules="requiredRule()"
            class="required"
            :placeholder="$t('please-select-domain')"
            v-on:change="filterSubDomains(index)"
            hide-details
            clearable
          >
          </v-autocomplete>
        </v-col>
        <v-col cols="10" md="5">
          <label>{{ $t('scientific-subdomain') }}</label>
          <p class="label-info">
            A named group of values for scientific subdomains within the defined domain.<br /><br />
          </p>
          <v-autocomplete
            v-model="item.subdomain"
            :items="filteredSubdomains[index]"
            :item-value="'id'"
            :item-text="'subDomain'"
            outlined
            :rules="requiredRule()"
            class="required"
            :placeholder="$t('please-select-subdomain')"
            hide-details
            clearable
          ></v-autocomplete>
        </v-col>
        <v-col cols="2" class="pt-15 mt-5" v-if="filteredSubdomains.length > 1">
          <v-btn fab small text @click="removeDomain(index)">
            <v-icon>
              $vuetify.icons.trash
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Add more Domains & Subdomains -->
      <v-row class="mb-4 mt-0 pt-0" v-if="hasSelectedDomain">
        <v-col class="mt-5">
          <v-btn
            @click="addDomain"
            depressed
            rounded
            large
            color="primary200"
            class="white--text font-weight-bold"
          >
            <v-icon left>
              $vuetify.icons.plus
            </v-icon>
            <span class="text-decoration-underline text-transform-none font-weight-bold">
              {{ $t('add-scientific-domain') }}
            </span>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Optional -->
      <v-row v-if="edit">
        <v-col cols="12">
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="mt-5">
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('tags') }}</label>
                    <p class="label-info">{{ $t('tags-message') }}</p>
                    <v-combobox
                      v-model="optionalProvider.tags"
                      clearable
                      :placeholder="'Select Tags'"
                      multiple
                      persistent-hint
                      small-chips
                      return-object
                      outlined
                      hint="Type to create a new tag"
                    >
                      <template v-slot:no-data>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>
                              Type and press <span class="text-bold">enter</span> to create a new
                              tag
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                      <template v-slot:selection="{ attrs, item, parent, selected }">
                        <v-chip
                          v-bind="attrs"
                          :input-value="selected"
                          :close="true"
                          @click:close="removeChip(item)"
                        >
                          <span class="pr-2">
                            {{ item }}
                          </span>
                        </v-chip>
                      </template>
                    </v-combobox>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('structure-type') }}</label>
                    <p class="label-info">{{ $t('structure-type-message') }}</p>
                    <v-autocomplete
                      v-model="optionalProvider.structureTypes"
                      :items="structureTypes"
                      :placeholder="$t('structure-type')"
                      :item-value="'id'"
                      :item-text="'name'"
                      return-object
                      outlined
                      clearable
                      multiple
                    ></v-autocomplete>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('area-of-activity') }}</label>
                    <p class="label-info">{{ $t('area-of-activity-type-message') }}</p>
                    <v-autocomplete
                      v-model="optionalProvider.areasOfActivities"
                      :items="areasOfActivities"
                      :placeholder="'Area of Activity'"
                      :item-value="'id'"
                      :item-text="'name'"
                      return-object
                      outlined
                      clearable
                      multiple
                    ></v-autocomplete>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Buttons -->
      <v-row class="my-5" justify="end">
        <v-btn
          v-if="unsavedChanges && edit"
          @click="cancel"
          outlined
          class="black--text font-weight-bold mx-2"
          rounded
          x-large
        >
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          v-if="unsavedChanges && edit"
          @click="updateProviderClassificationInfo"
          depressed
          rounded
          x-large
          color="primary200"
          class="white--text font-weight-bold"
        >
          {{ $t('update') }}
        </v-btn>
        <v-btn
          v-if="!unsavedChanges"
          depressed
          rounded
          x-large
          color="black"
          @click="back"
          outlined
          class="black--text font-weight-bold mx-2"
        >
          <v-icon color="black">$vuetify.icons.back</v-icon>
          {{ $t('back') }}
        </v-btn>
        <v-btn
          v-if="!unsavedChanges"
          depressed
          rounded
          x-large
          color="primary200"
          @click="next"
          class="white--text font-weight-bold"
          >{{ $t('next') }}
          <v-icon color="white">$vuetify.icons.next</v-icon>
        </v-btn>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import util from '@/mixins/util.js';
import base from '@/services/base-service.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';
import EventBus from '@/helpers/event-bus.js';

export default {
  name: 'AddProviderClassificationInfo',
  components: { AlertUnsavedChanges },
  mixins: [util, base],
  data: () => ({
    domainsList: [{ domain: '', subdomain: '' }],
    initialDomainList: [],
    domains: [],
    subdomains: [],
    filteredSubdomains: [],
    unsavedChanges: false,
    dataLoaded: false,
    optionalProvider: {
      tags: [],
      structureTypes: [],
      areasOfActivities: [],
    },
    initialTags: [],
    initialStructureTypes: [],
    initialAreasOfActivities: [],
    structureTypes: [],
    areasOfActivities: [],
  }),
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    this.getScientificDomains().then(res => {
      this.domains = res.data.content;
    });

    this.getScientificSubdomains().then(res => {
      this.subdomains = res.data.content;
    });

    this.getStructureTypes().then(res => {
      this.structureTypes = res.data.content;
    });

    this.getAreasOfActivities().then(res => {
      this.areasOfActivities = res.data.content;
    });

    if (this.edit) {
      this.getProvider();
    }
  },
  watch: {
    domainsList: {
      deep: true,
      handler(newList) {
        if (!this.dataLoaded) return;

        if (JSON.stringify(newList) !== JSON.stringify(this.initialDomainList)) {
          this.unsavedChanges = true;
        } else {
          this.unsavedChanges = false;
        }
      },
    },
    'optionalProvider.tags': {
      deep: true,
      handler(newTag) {
        if (!this.dataLoaded) return;

        if (JSON.stringify(newTag) !== JSON.stringify(this.initialTags)) {
          this.unsavedChanges = true;
        } else {
          this.unsavedChanges = false;
        }
      },
    },
    'optionalProvider.structureTypes': {
      deep: true,
      handler(newType) {
        if (!this.dataLoaded) return;

        if (JSON.stringify(newType) !== JSON.stringify(this.initialStructureTypes)) {
          this.unsavedChanges = true;
        } else {
          this.unsavedChanges = false;
        }
      },
    },
    'optionalProvider.areasOfActivities': {
      deep: true,
      handler(newActivity) {
        if (!this.dataLoaded) return;

        if (JSON.stringify(newActivity) !== JSON.stringify(this.initialAreasOfActivities)) {
          this.unsavedChanges = true;
        } else {
          this.unsavedChanges = false;
        }
      },
    },
  },
  computed: {
    hasSelectedDomain() {
      return this.domainsList.some(item => item.domain);
    },
  },
  methods: {
    filterSubDomains(index) {
      console.log('Domain changed:', this.domainsList[index].domain);

      this.$set(
        this.filteredSubdomains,
        index,
        this.subdomains.filter(subdomain => subdomain.domain === this.domainsList[index].domain),
      );

      this.domainsList[index].subdomain = '';
    },
    removeDomain(index) {
      if (this.domainsList.length > 1) {
        this.domainsList.splice(index, 1);
        this.filteredSubdomains.splice(index, 1);
      }
    },
    addDomain() {
      this.domainsList.push({ domain: '', subdomain: '' });
      this.filteredSubdomains.push([]);
    },
    back() {
      this.$emit('tab', 1);
    },
    next() {
      const isFormValid = this.$refs.classificationForm.validate();
      if (isFormValid) {
        const scientificsIds = this.domainsList.map(domain => domain.subdomain);

        this.$emit('tab', 3);
        this.$emit('providerClassificationInfo', {
          scientificsIds,
        });
      }
    },
    getProvider() {
      EventBus.$emit('waiting', true);
      const id = this.$route.params.id;

      this.axios
        .get('/api/v1/provider-settings/' + id)
        .then(response => {
          console.log('Get Provider Classification Info', response.data);
          const providerData = response.data;

          if (providerData.scientifics) {
            this.domainsList = providerData.scientifics.map(domain => ({
              domain: domain.domain,
              subdomain: domain.subDomain,
              id: domain.id,
            }));

            this.initialDomainList = JSON.parse(JSON.stringify(this.domainsList));

            this.domainsList.forEach((domain, index) => {
              this.$set(this.filteredSubdomains, index, [domain.subdomain]);
            });
          }

          if (providerData.tags) {
            this.optionalProvider.tags = providerData.tags;

            this.initialTags = JSON.parse(JSON.stringify(this.optionalProvider.tags));
          }

          if (providerData.structureTypes) {
            this.optionalProvider.structureTypes = providerData.structureTypes.map(type => ({
              id: type.id,
              name: type.name,
            }));

            this.initialStructureTypes = JSON.parse(
              JSON.stringify(this.optionalProvider.structureTypes),
            );
          }

          if (providerData.areasOfActivities) {
            this.optionalProvider.areasOfActivities = providerData.areasOfActivities.map(
              activity => ({
                id: activity.id,
                name: activity.name,
              }),
            );

            this.initialAreasOfActivities = JSON.parse(
              JSON.stringify(this.optionalProvider.areasOfActivities),
            );
          }

          this.dataLoaded = true;
        })
        .catch(error => {
          console.error('Error fetching provider data:', error);
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    cancel() {
      this.getProvider();
    },
    updateProviderClassificationInfo() {
      const id = this.$route.params.id;

      const scientificsIds = this.domainsList.map(domain => {
        const subdomain = this.subdomains.find(
          sub => sub.subDomain === domain.subdomain && sub.domain === domain.domain,
        );
        return subdomain ? subdomain.id : domain.subdomain;
      });

      const tags = this.optionalProvider.tags.map(tag => tag);

      const structureTypes = this.optionalProvider.structureTypes.map(type => ({
        id: type.id,
        name: type.name,
      }));

      const areasOfActivities = this.optionalProvider.areasOfActivities.map(activity => ({
        id: activity.id,
        name: activity.name,
      }));

      const providerUpdateData = {
        id: id,
        scientificsIds: scientificsIds,
        tags: tags,
        structureTypes: structureTypes,
        areasOfActivities: areasOfActivities,
      };

      this.axios
        .post('/api/v1/provider-settings/classification-info', providerUpdateData)
        .then(response => {
          console.log('Provider classification updated successfully:', response.data);
          EventBus.$emit('snackbar', {
            text: 'Provider Updated Successfully',
            color: 'primary200',
          });

          this.unsavedChanges = false;

          this.$emit('updatedChanges', true);
        })
        .catch(error => {
          console.error('Error updating provider info:', error);
        });
    },
    removeChip(itemText) {
      this.optionalProvider.tags.forEach(obj => {
        if (obj === itemText) {
          this.optionalProvider.tags.splice(this.optionalProvider.tags.indexOf(obj), 1);
        }
      });
      this.optionalProvider.tags = [...this.optionalProvider.tags];
    },
  },
};
</script>
